import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';
import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  type AttachmentReadContent,
  type AttachmentPresentation,
  type AttachmentItemContent
} from './attachment-read-content.ts';
import { AttachmentValueView } from './attachment-value-view.tsx';

export type AttachmentReadRenderer = (content: AttachmentReadContent) => ReactNode;
export type AttachmentReadPolicy = {
  readonly getAttachmentResource?: (
    fileId: string
  ) => FieldReadResource<AttachmentPresentation>;
};

const renderAttachment: AttachmentReadRenderer = (content) =>
  createElement(AttachmentValueView, { content });

/**
 * Compile format once; only mounted Media API values declare external reads.
 */
export function createAttachmentFieldReadModule(
  options?: AttachmentReadPolicy & { readonly render?: AttachmentReadRenderer }
) {
  const { render = renderAttachment, getAttachmentResource } = options ?? {};

  return defineFieldReadModule<AttachmentReadContent>({
    fieldType: DatabaseFieldType.ATTACHMENT,
    render,
    compile(field: DatabaseField) {
      const mediaFormat = field.settings.mediaFormat;
      const empty: AttachmentReadContent = Object.freeze({
        kind: 'attachment',
        mediaFormat,
        items: Object.freeze([])
      });
      const resolve = (stored: DatabaseValue | undefined): AttachmentReadContent => {
        if (stored?.type !== DatabaseFieldType.ATTACHMENT || !stored.value.length) {
          return empty;
        }

        return Object.freeze({
          kind: 'attachment',
          mediaFormat,
          items: Object.freeze(
            stored.value.map((item) =>
              Object.freeze<AttachmentItemContent>({
                key: item._id,
                fileId: 'fileId' in item ? item.fileId : undefined,
                presentation: undefined
              })
            )
          )
        });
      };

      return Object.freeze({
        fieldId: field.id,
        resolve,
        getResourceRead({ storedValue }) {
          if (!getAttachmentResource) {
            return undefined;
          }

          const initial = resolve(storedValue);
          const resources = new Map<string, FieldReadResource<AttachmentPresentation>>();

          for (const item of initial.items) {
            if (item.fileId !== undefined && !resources.has(item.fileId)) {
              resources.set(item.fileId, getAttachmentResource(item.fileId));
            }
          }

          if (!resources.size) {
            return undefined;
          }

          return Object.freeze({
            resources: Object.freeze([...resources.values()]),
            resolve(
              read: <Value>(
                resource: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              let changed = false;
              const items = initial.items.map((item) => {
                const resource =
                  item.fileId === undefined ? undefined : resources.get(item.fileId);

                if (!resource) {
                  return item;
                }

                const state = read(resource);

                if (state.status !== 'ready') {
                  return item;
                }

                changed = true;

                return Object.freeze({ ...item, presentation: state.value });
              });

              return changed
                ? Object.freeze({ ...initial, items: Object.freeze(items) })
                : initial;
            }
          });
        }
      });
    }
  });
}
